/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';
import Table from 'components/Table';
import utils from 'utils';
import { TableDescription, TableTitle, TableWrapper } from '../routes/Chart/style';
import { CHART_TYPE } from 'utils/constants';
import { Series } from '../interfaces';

interface TableAreaProps {
  series: Series[];
  checkboxedRowNames?: string[];
  chartType?: string;
  selectedName?: string;
  setSelectedName: (name: string | undefined) => void;
  setCheckboxedRowNames: (names: string[]) => void;
}

const TableArea = ({
  checkboxedRowNames,
  chartType,
  series,
  setCheckboxedRowNames,
  setSelectedName,
  selectedName,
}: TableAreaProps) => {
  const t = utils.intl.useTranslator();

  const { histogram, line, bar, pie, stackedBar, area } = CHART_TYPE;
  const type = chartType?.toLowerCase();
  const isHistogramChart = type === histogram;
  const isLineChart = type === line;
  const isMultiBarChart = type === bar;
  const isPieChart = type === pie;
  const isStackedBarChart = type === stackedBar;
  const isAreaChart = type?.toLowerCase() === area?.toLowerCase();

  useEffect(() => {
    if (!series) return;

    let initialTableState: string[] = [];
    if (isLineChart || isHistogramChart || isStackedBarChart || isAreaChart) {
      initialTableState = series?.map(({ name }) => name).slice(0, 3) || [];
    }
    if (isMultiBarChart || isPieChart) {
      initialTableState = series?.[0].data.map(({ x }) => x).slice(0, 3) || [];
    }

    setCheckboxedRowNames(initialTableState);
  }, [
    series,
    isHistogramChart,
    isLineChart,
    isPieChart,
    isMultiBarChart,
    isStackedBarChart,
    isAreaChart,
    setCheckboxedRowNames,
  ]);

  const [selectedRowIndex, setSelectedRowIndex] = useState<number>();

  const cell = (props: { value: string; row: { original: { name: string } } }) => {
    const { value, row } = props;

    const selectTableRow = () => {
      const rowName = row.original.name;
      setCheckboxedRowNames([...(checkboxedRowNames as string[]), rowName]);
      setSelectedName(selectedName === rowName ? undefined : rowName);
    };

    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          if (isLineChart) {
            selectTableRow();
          }
        }}
      >
        {value}
      </div>
    );
  };

  const seriesNoAverage = series.filter(({ average }) => !average);

  const groupData = (series: Series[]) => {
    const groupedData: { [key: string]: { [key: string]: number | string }[] } = {};

    series.forEach(({ data, name }) => {
      data.forEach(({ x, y }) => {
        if (!groupedData[x]) {
          groupedData[x] = [];
        }
        groupedData[x].push({ seriesName: name, value: y });
      });
    });

    return Object.entries(groupedData).map(([x, y]) => ({ x, y }));
  };

  const tableConfig = {
    histogram: {
      columns: () => [
        {
          accessor: 'company',
          Header: t('chartTableCompanyColumn'),
          width: 300,
          Cell: cell,
        },
        {
          accessor: 'accuracy',
          Header: t('chartTableAccuracyColumn'),
          width: 300,
          Cell: cell,
        },
      ],
      rows: () =>
        series.flatMap(({ data, name }) =>
          data.map((item) => ({
            name,
            company: item.x,
            accuracy: item.formattedY,
          })),
        ),
    },
    line: {
      columns: () => [
        {
          accessor: 'label',
          Header: t('chartTableLabelColumn'),
          width: 300,
          Cell: cell,
        },
        {
          accessor: 'description',
          Header: t('chartTableDescriptionColumn'),
          width: 300,
          Cell: cell,
        },
      ],
      rows: () =>
        seriesNoAverage.map(({ label, name, description }) => ({
          label,
          description,
          name,
        })),
    },
    stackedbar: {
      columns: () => [
        {
          accessor: 'label',
          Header: t('chartTableLabelColumn'),
          width: 300,
          Cell: cell,
        },
        {
          accessor: 'description',
          Header: t('chartTableDescriptionColumn'),
          width: 300,
          Cell: cell,
        },
      ],
      rows: () =>
        series.map(({ label, name, description }) => ({
          label,
          description,
          name,
        })),
    },
    stackedarea: {
      columns: () => [
        {
          accessor: 'label',
          Header: t('chartAreaTableLabelColumn'),
          width: 300,
          Cell: cell,
        },
        {
          accessor: 'description',
          Header: t('chartAreaTableDescriptionColumn'),
          width: 300,
          Cell: cell,
        },
      ],
      rows: () =>
        series.map(({ label, name, description }) => ({
          label,
          description,
          name,
        })),
    },
    bar: {
      columns: () => [
        {
          accessor: 'name',
          Header: t('chartTableNameColumn'),
          width: 300,
          Cell: cell,
        },
        ...series.map(({ name }, index) => ({
          accessor: name,
          Header: t(`chartTableMultiBarValue${index + 1}Column`),
          width: 300,
          Cell: cell,
        })),
      ],
      rows: () =>
        groupData(series).map(({ x, y }) => ({
          name: x,
          ...y.reduce((acc, { seriesName, value }) => {
            acc[seriesName] = value;
            return acc;
          }, {}),
        })),
    },
    pie: {
      columns: () => [
        {
          accessor: 'name',
          Header: t('pieTableCompanyColumn'),
          width: 300,
          Cell: cell,
        },
        {
          accessor: 'accuracy',
          Header: t('pieTableAccuracyColumn'),
          width: 300,
          Cell: cell,
        },
      ],
      rows: () =>
        series[0].data.map(({ x, formattedY }) => ({
          name: x,
          accuracy: formattedY,
        })),
    },
  };

  const onCheckboxChange = (values: { [key: string]: string }[]) => {
    const names = values.map(({ name }) => name);
    setCheckboxedRowNames(names);
  };

  const onChange = (props: { id: string }[]) => {
    setSelectedRowIndex(props.findIndex(({ id }) => id === selectedName));
  };

  return (
    <>
      {isHistogramChart && (
        <>
          <TableTitle>{t('histogramChartTableTitle')}</TableTitle>
          <TableDescription>{t('histogramChartTableDescription')}</TableDescription>
        </>
      )}
      {chartType && tableConfig[type as keyof typeof tableConfig] && (
        <TableWrapper selectedIndex={selectedRowIndex}>
          <Table
            id="table-area-table"
            key={chartType}
            rowId="name"
            initialSelection={checkboxedRowNames}
            columns={tableConfig[type as keyof typeof tableConfig]?.columns()}
            rows={tableConfig[type as keyof typeof tableConfig]?.rows()}
            hasMultiSelection
            hasFilter
            hasSort
            onChange={onChange}
            onSelection={onCheckboxChange}
            hasExport={false}
            hasPagination
          />
        </TableWrapper>
      )}
    </>
  );
};

export default TableArea;
